import React, { useMemo, useState, useEffect } from 'react';
import { Stack, TextField, Box, Typography, Avatar, IconButton, Tooltip, Divider, Card } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddMemberBar from './AddMemberBar';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';

const EditGroupInline = ({ group, onNameChange, onDescriptionChange, onMemberAdded, onMemberRemoved, onLeaderChanged, onRemoveMemberClick, navigate }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');

  useEffect(() => {
    if (group) {
      const newName = group.name || '';
      const newDescription = group.description || '';
      setName(newName);
      setDescription(newDescription);
      // Parent component'e başlangıç değerlerini bildir
      onNameChange?.(newName);
      onDescriptionChange?.(newDescription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
          Grup Adı
        </Typography>
        <TextField
          fullWidth
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            onNameChange?.(e.target.value);
          }}
          placeholder=""
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '5px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0E4D94',
              },
            },
            '& .MuiInputBase-input': {
              py: 1.2,
              px: 1.5,
              fontSize: '14px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e6e6e6',
            },
          }}
        />
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
          Açıklama
        </Typography>
        <TextField
          fullWidth
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            onDescriptionChange?.(e.target.value);
          }}
          placeholder=""
          multiline
          minRows={4}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '5px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0E4D94',
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '14px',
              px: 0,
              py: 0
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e6e6e6',
            },
          }}
        />
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box>
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
          Üyeler
        </Typography>
        <AddMemberBar
          groupId={group?.id}
          onAdded={onMemberAdded}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2,
            mt: 2,
          }}
        >
          {group?.members?.map((m) => {
            const isLeader = group.leaderId === m.id;
            // Partner bilgisini kontrol et
            const partner = m.subMemberFullName && m.subMemberProfilePicture
              ? { fullName: m.subMemberFullName, profilePicture: m.subMemberProfilePicture }
              : (m.subMembers && m.subMembers.length > 0 ? m.subMembers[0] : null);

            return (
              <Card
                key={m.id}
                sx={{
                  p: 2,
                  border: '1px solid #e6e6e6',
                  boxShadow: 'none',
                  borderRadius: '8px',
                  position: 'relative',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                  },
                }}
              >
                {/* Action Buttons */}
                <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8 }}>
                  {!isLeader && (
                    <Tooltip title="Lider Yap">
                      <IconButton
                        size="small"
                        sx={{
                          p: 0.5,
                          backgroundColor: 'transparent',
                          width: 24,
                          height: 24,
                          '&:hover': {
                            backgroundColor: '#FFF8E1',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onLeaderChanged?.(m.id);
                        }}
                      >
                        <StarBorderIcon sx={{ fontSize: 14, color: '#FFA500' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Gruptan kaldır">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveMemberClick?.(m);
                      }}
                      sx={{
                        backgroundColor: '#FFEEF3',
                        borderRadius: '6px',
                        width: 24,
                        height: 24,
                        '&:hover': {
                          backgroundColor: '#FFD6E0',
                        },
                        '& svg': {
                          fontSize: '14px',
                          color: '#F8285A',
                        },
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {/* Avatar'lar ve İsimler */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {/* Avatar'lar */}
                  <Stack direction="row" spacing={.3} alignItems="center">
                    <Avatar
                      src={m.profilePicture ? resolveGroupsAssetUrl(m.profilePicture) : undefined}
                      alt={m.fullName}
                      sx={{
                        width: 48,
                        height: 48,
                        cursor: 'pointer',
                        border: '2px solid #e6e6e6',
                      }}
                      onClick={() => navigate?.(`/members/${m.id}`)}
                    >
                      {!m.profilePicture && (
                        <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                          {m.fullName?.charAt(0) || '?'}
                        </Typography>
                      )}
                    </Avatar>
                    {partner && (
                      <Avatar
                        src={partner.profilePicture ? resolveGroupsAssetUrl(partner.profilePicture) : undefined}
                        alt={partner.fullName}
                        sx={{
                          width: 48,
                          height: 48,
                          cursor: 'pointer',
                          border: '2px solid #e6e6e6',
                        }}
                        onClick={() => navigate?.(`/members/${m.id}`)}
                      >
                        {!partner.profilePicture && (
                          <Typography sx={{ fontSize: '18px', color: '#0E4D94', fontWeight: 600 }}>
                            {partner.fullName?.charAt(0) || '?'}
                          </Typography>
                        )}
                      </Avatar>
                    )}
                  </Stack>

                  {/* İsimler */}
                  <Stack spacing={0.3} sx={{ flex: 1, cursor: 'pointer' }} onClick={() => navigate?.(`/members/${m.id}`)}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                        {m.fullName}
                      </Typography>
                      {isLeader && (
                        <Tooltip title="Grup Lideri">
                          <StarIcon sx={{ color: '#FFA500', fontSize: 16 }} />
                        </Tooltip>
                      )}
                    </Stack>
                    {partner && (
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                        {partner.fullName}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
};

export default EditGroupInline;


